import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { User } from '@/types'
import { authApi } from '@/api'
import { logEnteredLogin } from '@/utils/sessionLogger'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const loading = ref(false)
  const selectedExtension = ref<string | null>(null)
  const selectedLanguages = ref<string[]>([])

  const isAuthenticated = computed(() => !!token.value)
  const extensionStorageKey = computed(() => {
    const login = user.value?.login
    return login ? `ui.selectedExtension.${login}` : null
  })
  const languagesStorageKey = computed(() => {
    const login = user.value?.login
    return login ? `ui.selectedLanguages.${login}` : null
  })

  watch(extensionStorageKey, key => {
    if (!key) {
      selectedExtension.value = null
      return
    }
    const saved = localStorage.getItem(key)
    selectedExtension.value = saved || null
  }, { immediate: true })

  watch(selectedExtension, value => {
    const key = extensionStorageKey.value
    if (!key) return
    if (value) {
      localStorage.setItem(key, value)
    } else {
      localStorage.removeItem(key)
    }
  })

  watch(languagesStorageKey, key => {
    if (!key) {
      selectedLanguages.value = []
      return
    }
    const saved = localStorage.getItem(key)
    if (!saved) {
      selectedLanguages.value = []
      return
    }
    try {
      const parsed = JSON.parse(saved)
      selectedLanguages.value = Array.isArray(parsed) ? parsed : []
    } catch {
      selectedLanguages.value = []
    }
  }, { immediate: true })

  watch(selectedLanguages, value => {
    const key = languagesStorageKey.value
    if (!key) return
    localStorage.setItem(key, JSON.stringify(value || []))
  }, { deep: true })

  async function login(username: string, password: string, remember = false) {
    loading.value = true
    logEnteredLogin(username)
    try {
      const response = await authApi.login(username, password)
      token.value = response.access_token
      user.value = response.user
      
      if (remember) {
        localStorage.setItem('token', response.access_token)
      } else {
        sessionStorage.setItem('token', response.access_token)
      }
      
      return true
    } catch (error) {
      console.error('Login failed:', error)
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    token.value = null
    user.value = null
    selectedExtension.value = null
    selectedLanguages.value = []
    localStorage.removeItem('token')
    sessionStorage.removeItem('token')
  }

  async function checkAuth() {
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (storedToken) {
      token.value = storedToken
      try {
        user.value = await authApi.getProfile()
      } catch {
        logout()
      }
    }
  }

  return {
    user,
    token,
    loading,
    isAuthenticated,
    selectedExtension,
    selectedLanguages,
    login,
    logout,
    checkAuth
  }
})
